import { renderNav } from '../components/nav.js';
import { DOCS, isKnownDoc, renderDocsLayout } from '../components/docs-layout.js';
import { renderMarkdown } from '../lib/markdown.js';

const sources = import.meta.glob('../../../docs/*.md', {
  eager: true,
  query: '?raw',
  import: 'default',
}) as Record<string, string>;

const rendered: Record<string, string> = {};
for (const [path, source] of Object.entries(sources)) {
  const match = path.match(/\/([^/]+)\.md$/);
  if (match === null) continue;
  const slug = match[1] ?? '';
  if (slug === '' || slug === 'faq') continue;
  if (!isKnownDoc(slug)) continue;
  rendered[slug] = renderMarkdown(source);
}

function bindInPageAnchors(root: HTMLElement, routePrefix: string): void {
  root.querySelectorAll('a').forEach((a) => {
    const href = a.getAttribute('href');
    if (href === null) return;
    if (!href.startsWith('#') || href.startsWith('#/')) return;
    const id = decodeURIComponent(href.slice(1));
    a.addEventListener('click', (e) => {
      e.preventDefault();
      const target = document.getElementById(id);
      if (target !== null) {
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
      window.history.replaceState(null, '', `${routePrefix}#${id}`);
    });
  });
}

function scrollToSection(section: string | null): void {
  if (section === null || section === '') return;
  requestAnimationFrame(() => {
    const el = document.getElementById(section);
    if (el !== null) el.scrollIntoView({ behavior: 'auto', block: 'start' });
  });
}

function notFoundHtml(slug: string): string {
  return `
    <div class="docs-error">
      <h1>Not found</h1>
      <p>No doc found at <code>${slug.replace(/[<>&]/g, '')}</code>.</p>
      <p><a href="#/docs">Back to docs</a></p>
    </div>
  `;
}

export function mountDocDetail(root: HTMLElement, slug: string, section: string | null): () => void {
  const html = rendered[slug];
  if (html === undefined) {
    root.innerHTML = `
      ${renderNav({ active: 'docs' })}
      ${renderDocsLayout(null, notFoundHtml(slug))}
    `;
    return () => {};
  }
  const entry = DOCS.find((d) => d.slug === slug);
  const title = entry?.title ?? slug;
  const body = `
    <article class="markdown-body docs-article">
      ${html}
    </article>
  `;
  root.innerHTML = `
    ${renderNav({ active: 'docs' })}
    ${renderDocsLayout(slug, body)}
  `;
  document.title = `${title} · The Flock`;
  const article = root.querySelector<HTMLElement>('.docs-article');
  if (article !== null) bindInPageAnchors(article, `#/docs/${slug}`);
  scrollToSection(section);
  return () => {
    document.title = 'The Flock';
  };
}
